import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import useScrollRestoration from "@/hooks/useScrollRestoration";
import { useAuth } from "@/context/AuthContext";
import { LockedPanel } from "@/components/entitlements/LockedPanel";
import {
  Activity,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Trophy,
  GitCompare,
  Network,
  Landmark,
  Briefcase,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** New WHIP landing page. ESPN-style multi-panel dashboard.
 *  Replaces the prior Bills index at "/". Bills index has moved to
 *  "/legislation".
 */
export default function LandingDashboard() {
  // `featuredItems` is a list (carousel). The older endpoint shape was a
  // single object, we've migrated the backend to return `{items: [...]}`
  // but gracefully fall back if a deployed server still returns an object.
  const { featureMode } = useAuth();
  const forecastLocked = featureMode("whip_probability") === "none";
  const [featuredItems, setFeaturedItems] = useState([]);
  const [signals, setSignals] = useState([]);
  const [trending, setTrending] = useState([]);
  const [topLegs, setTopLegs] = useState([]);
  const [justIntroduced, setJustIntroduced] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore scroll-Y when the user navigates back from a bill or legislator
  // page. Single key (the landing page is shared across all visitors and
  // has no per-user state) and only marked ready once the carousel + side
  // panels have hydrated so the page is at full height.
  const { saveScroll } = useScrollRestoration(
    "landing-dashboard",
    !loading && featuredItems.length > 0
  );

  useEffect(() => {
    // Progressive load: the hero unblocks as soon as `featured` returns, // side panels (signals / trending / rankings / just-introduced) hydrate
    // independently so one slow endpoint never gates first paint.
    if (forecastLocked) { setLoading(false); return; }
    axios
      .get(`${API}/dashboard/featured`, { params: { limit: 8 } })
      .then((f) => {
        const data = f.data || {};
        // New shape: { items: [...] }. Legacy shape: {...bill} (single).
        const items = Array.isArray(data.items)
          ? data.items
          : (data && data.billId ? [data] : []);
        setFeaturedItems(items);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    axios
      .get(`${API}/dashboard/signals`, { params: { limit: 8 } })
      .then((s) => setSignals(s.data || []))
      .catch(() => {});
    axios
      .get(`${API}/dashboard/trending`, { params: { limit: 6 } })
      .then((t) => setTrending(t.data || []))
      .catch(() => {});
    axios
      .get(`${API}/legislators/rankings/bipartisan`, { params: { limit: 5 } })
      .then((l) => setTopLegs(l.data || []))
      .catch(() => {});
    axios
      .get(`${API}/dashboard/just-introduced`, { params: { within_hours: 72, limit_per_area: 4 } })
      .then((ji) => setJustIntroduced(ji.data || null))
      .catch(() => {});
  }, [forecastLocked]);

  const verdictColor = (v) => {
    if (v === "Likely to Pass") return { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-300" };
    if (v === "Passed" || v === "Enacted") return { bg: "bg-emerald-500/20", border: "border-emerald-400/50", text: "text-emerald-200" };
    if (v === "Likely to Fail") return { bg: "bg-rose-500/10", border: "border-rose-500/30", text: "text-rose-300" };
    if (v === "Stalled") return { bg: "bg-purple-500/10", border: "border-purple-500/30", text: "text-purple-300" };
    return { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-300" };
  };

  const partyClass = (p) => {
    if (p === "D") return "bg-[#2F6BFF]/15 text-blue-300 border-[#2F6BFF]/30";
    if (p === "R") return "bg-[#FF3B3B]/15 text-rose-300 border-[#FF3B3B]/30";
    if (p === "I") return "bg-purple-500/15 text-purple-300 border-purple-500/30";
    return "bg-white/5 text-white/60 border-white/15";
  };

  // Carousel logic, horizontal scroll with arrow buttons, scroll-snap.
  const railRef = useRef(null);
  const scrollBy = (dir) => {
    const el = railRef.current;
    if (!el) return;
    // Scroll ~90% of the viewport width so the next card snaps in cleanly.
    const amount = Math.max(320, el.clientWidth * 0.9);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  // HERO auto-rotating featured bill. Cycles through `featuredItems` every
  // 6s. Pauses on hover + when the tab is hidden so we don't waste energy
  // when nobody's watching.
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  const [signalIdx, setSignalIdx] = useState(0);
  useEffect(() => {
    // Reset to first card whenever the underlying list changes so we don't
    // land out-of-bounds after a refetch.
    setHeroIdx(0);
  }, [featuredItems.length]);
  useEffect(() => {
    if (featuredItems.length < 2 || heroPaused) return;
    const id = setInterval(() => {
      if (document.hidden) return;
      setHeroIdx((i) => (i + 1) % featuredItems.length);
    }, 6000);
    return () => clearInterval(id);
  }, [featuredItems.length, heroPaused]);

  const heroPrev = () =>
    setHeroIdx((i) => (i - 1 + featuredItems.length) % featuredItems.length);
  const heroNext = () =>
    setHeroIdx((i) => (i + 1) % featuredItems.length);

  // Keyboard arrows, only fire when the hero card has focus or hover so
  // they don't fight with the page-level scroll/nav.
  useEffect(() => {
    if (featuredItems.length < 2) return;
    const n = featuredItems.length;
    const onKey = (e) => {
      if (!heroPaused) return; // only steal arrows while hovering the card
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setHeroIdx((i) => (i - 1 + n) % n);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setHeroIdx((i) => (i + 1) % n);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [featuredItems.length, heroPaused]);

  // Touch swipe, track touchstart x, fire on touchend if delta ≥ 40px.
  const heroTouchX = useRef(null);
  const onHeroTouchStart = (e) => {
    heroTouchX.current = e.touches?.[0]?.clientX ?? null;
    setHeroPaused(true);
  };
  const onHeroTouchEnd = (e) => {
    const startX = heroTouchX.current;
    heroTouchX.current = null;
    setHeroPaused(false);
    if (startX == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? startX;
    const dx = endX - startX;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) heroNext();
    else heroPrev();
  };

  const heroItem = featuredItems[heroIdx] || featuredItems[0];
  const fpct = heroItem?.passProbability != null
    ? Math.round(heroItem.passProbability * 100)
    : null;

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }} data-testid="landing-dashboard">
      <Navigation />

      {/* HERO, left: pitch, right: featured bill probability */}
      <section
        className="border-b border-white/5 relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse 1200px 600px at 80% -20%, rgba(255,138,0,0.10), transparent 60%), radial-gradient(ellipse 800px 500px at 0% 100%, rgba(47,107,255,0.06), transparent 60%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="whip-pill whip-pill-orange flex items-center gap-1.5" data-testid="hero-eyebrow">
                <Activity className="w-3 h-3" />
                LIVE INTELLIGENCE
              </span>
              <span className="text-[11px] uppercase tracking-[0.18em] text-white/40">119th Congress</span>
            </div>
            <h1 className="text-[2.6rem] sm:text-6xl lg:text-7xl font-black tracking-tight text-white leading-[0.95]">
              Know the vote
              <br />
              <span className="whip-orange-text">before it happens.</span>
            </h1>
            <p className="text-base sm:text-lg text-white/65 mt-6 max-w-xl leading-relaxed">
              Real-time legislative intelligence and vote prediction for every bill in the U.S. Congress.
              Probability, power, and movement at a glance.
            </p>
            <div className="flex items-center gap-3 mt-8 flex-wrap" data-testid="hero-actions">
              <Link
                to="/legislation"
                className="inline-flex items-center justify-center whip-orange-bg hover:opacity-95 text-white border border-orange-400 px-6 py-3 text-base font-semibold rounded-md transition-opacity"
                data-testid="hero-cta-legislation"
              >
                Track legislation <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link
                to="/legislators"
                className="inline-flex items-center justify-center border border-white/20 text-white hover:bg-white/5 px-6 py-3 text-base font-semibold rounded-md transition-colors"
                data-testid="hero-cta-profiles"
              >
                Browse profiles
              </Link>
            </div>
          </div>

          {/* Featured bill, auto-rotating hero card */}
          <div className="lg:col-span-5 min-w-0">
            {forecastLocked ? (
              <LockedPanel feature="whip_probability" tier="pro" title="Featured Forecast"
                blurb="Live WHIP probability, expected vote count and the bills most likely to move next." className="h-full" />
            ) : featuredItems.length > 0 && heroItem ? (
              (() => {
                const first = heroItem;
                const vc = verdictColor(first.verdict);
                return (
                  <Card
                    className="p-6 rounded-2xl border border-white/10 backdrop-blur whip-glow relative overflow-hidden transition-opacity duration-500"
                    style={{ background: "linear-gradient(160deg, rgba(255,138,0,0.10), rgba(11,11,15,0.90))" }}
                    data-testid="hero-featured-card"
                    onMouseEnter={() => setHeroPaused(true)}
                    onMouseLeave={() => setHeroPaused(false)}
                    onTouchStart={onHeroTouchStart}
                    onTouchEnd={onHeroTouchEnd}
                    tabIndex={0}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-orange-300 flex items-center gap-2">
                        Featured bill
                        {featuredItems.length > 1 && (
                          <span className="text-white/30 normal-case tracking-normal" data-testid="hero-featured-counter">
                            {heroIdx + 1} / {featuredItems.length}
                          </span>
                        )}
                      </span>
                      <Badge className={`${vc.bg} ${vc.text} ${vc.border} border px-2 py-0.5`}>
                        {first.verdict || first.status}
                      </Badge>
                    </div>
                    {/* Key on heroIdx so React remounts subtree → triggers fade-in */}
                    <div key={heroIdx} className="whip-hero-fade">
                      <Link to={`/bill/${first.billId}`} onClick={saveScroll} className="block group" data-testid="hero-featured-link">
                        <div className="text-2xl font-black text-white tracking-tight group-hover:text-orange-200 transition-colors">
                          {first.billNumber || first.billId}
                        </div>
                        <div className="text-sm text-white/70 line-clamp-2 mt-1 mb-5">
                          {first.title || "Loading bill metadata…"}
                        </div>
                      </Link>

                      {fpct != null && (
                        <div className="space-y-2 mb-3" data-testid="hero-featured-prob">
                          <div className="flex items-baseline justify-between">
                            <span className="text-[11px] uppercase tracking-wider text-white/45">WHIP Probability</span>
                            <span className="text-4xl sm:text-5xl font-black whip-orange-text tabular-nums">{fpct}%</span>
                          </div>
                          <div className="relative h-3 rounded-full bg-white/5 overflow-hidden">
                            <div
                              className="absolute left-0 top-0 h-full rounded-full"
                              style={{
                                width: `${fpct}%`,
                                background: "linear-gradient(90deg,#FF6A00,#FFA500)",
                                boxShadow: "0 0 18px rgba(255,138,0,0.6)",
                              }}
                            />
                          </div>
                          <div className="flex items-center justify-between text-xs text-white/55 tabular-nums">
                            <span>~{Math.round(first.expectedYes ?? 0)} yes</span>
                            <span>threshold {first.threshold ?? "n/a"}</span>
                            <span>~{Math.round(first.expectedNo ?? 0)} no</span>
                          </div>
                        </div>
                      )}

                      {first.sponsor?.name && (
                        <div className="flex items-center justify-between gap-3 text-xs text-white/45 pt-3 border-t border-white/5">
                          <div className="truncate">
                            Sponsor:&nbsp;
                            <span className={`px-1.5 py-0.5 rounded text-[10px] mr-1 border ${partyClass(first.sponsor.party)}`}>
                              {first.sponsor.party || "?"}
                            </span>
                            {first.sponsor.name} · {first.sponsor.state}
                          </div>
                          {first.daysSinceLastAction != null && (
                            <div className="text-white/35 sm:whitespace-nowrap" data-testid="hero-featured-recency">
                              Last action {first.daysSinceLastAction === 0
                                ? "today"
                                : `${first.daysSinceLastAction}d ago`}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Inline prev/next arrows, appear only when paused (hover/focus) */}
                    {featuredItems.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={heroPrev}
                          aria-label="Previous featured bill"
                          className="absolute top-1/2 -translate-y-1/2 left-2 w-8 h-8 rounded-full bg-black/50 border border-white/15 text-white/80 hover:bg-orange-500/30 hover:border-orange-400/60 hover:text-white transition-all opacity-0 group-hover:opacity-100 flex items-center justify-center"
                          style={{ opacity: heroPaused ? 1 : 0 }}
                          data-testid="hero-featured-prev"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={heroNext}
                          aria-label="Next featured bill"
                          className="absolute top-1/2 -translate-y-1/2 right-2 w-8 h-8 rounded-full bg-black/50 border border-white/15 text-white/80 hover:bg-orange-500/30 hover:border-orange-400/60 hover:text-white transition-all flex items-center justify-center"
                          style={{ opacity: heroPaused ? 1 : 0 }}
                          data-testid="hero-featured-next"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </>
                    )}

                    {/* Dot indicators, click to jump, auto-advances via timer */}
                    {featuredItems.length > 1 && (
                      <div
                        className="flex items-center justify-center gap-1.5 pt-4 mt-4 border-t border-white/5"
                        data-testid="hero-featured-dots"
                      >
                        {featuredItems.map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setHeroIdx(i)}
                            aria-label={`Jump to featured bill ${i + 1}`}
                            className="h-1.5 rounded-full transition-all"
                            style={{
                              width: i === heroIdx ? 22 : 6,
                              background: i === heroIdx ? "#FF8A00" : "rgba(255,255,255,0.2)",
                            }}
                            data-testid={`hero-featured-dot-${i}`}
                          />
                        ))}
                      </div>
                    )}

                    {/* Slim progress bar that fills over 6s to signal auto-advance */}
                    {featuredItems.length > 1 && !heroPaused && (
                      <div
                        key={`bar-${heroIdx}`}
                        className="absolute bottom-0 left-0 h-0.5 whip-hero-progress"
                        aria-hidden="true"
                      />
                    )}
                  </Card>
                );
              })()
            ) : (
              <Card className="p-8 rounded-2xl border border-white/10 bg-white/[0.03] text-center text-white/55">
                {loading ? "Pulling featured bill…" : "No featured bill cached yet."}
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* FEATURED BILLS CAROUSEL. Apple-TV-style horizontal rail */}
      {!forecastLocked && featuredItems.length > 1 && (
        <section className="border-b border-white/5 py-10" data-testid="featured-carousel-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-400" />
                  Bills to Watch
                </h2>
                <p className="text-sm text-white/50 mt-1">
                  Top forecast probability, swipe through the ones most likely to pass next.
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => scrollBy(-1)}
                  aria-label="Scroll left"
                  data-testid="carousel-prev"
                  className="w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/30 text-white flex items-center justify-center transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollBy(1)}
                  aria-label="Scroll right"
                  data-testid="carousel-next"
                  className="w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/30 text-white flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Rail */}
            <div
              ref={railRef}
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-thin"
              style={{ scrollbarWidth: "thin" }}
              data-testid="featured-carousel-rail"
            >
              {featuredItems.map((item, idx) => {
                const pct = item.passProbability != null ? Math.round(item.passProbability * 100) : null;
                const vc = verdictColor(item.verdict);
                return (
                  <Link
                    key={item.billId}
                    to={`/bill/${item.billId}`}
                    onClick={saveScroll}
                    data-testid={`featured-card-${idx + 1}`}
                    className="snap-start flex-shrink-0 w-[320px] sm:w-[360px] group"
                  >
                    <Card
                      className="h-full p-5 rounded-xl border border-white/10 hover:border-orange-400/60 transition-all"
                      style={{
                        background: "linear-gradient(155deg, rgba(18,18,26,0.95), rgba(11,11,15,0.95))",
                      }}
                    >
                      <div className="flex items-start justify-between mb-2.5">
                        <span className="text-[10px] uppercase tracking-[0.14em] text-white/40">
                          #{idx + 1} · {item.chamber || "Congress"}
                        </span>
                        <Badge className={`${vc.bg} ${vc.text} ${vc.border} border text-[10px] px-2 py-0.5`}>
                          {item.verdict || item.status}
                        </Badge>
                      </div>
                      <div className="text-lg font-black text-white tracking-tight group-hover:text-orange-200 transition-colors">
                        {item.billNumber || item.billId}
                      </div>
                      <div className="text-[13px] text-white/60 line-clamp-2 mt-1 mb-4 min-h-[36px]">
                        {item.title}
                      </div>

                      {pct != null ? (
                        <>
                          <div className="flex items-baseline justify-between mb-1.5">
                            <span className="text-[10px] uppercase tracking-wider text-white/45">Probability</span>
                            <span className="text-3xl font-black whip-orange-text tabular-nums leading-none">
                              {pct}%
                            </span>
                          </div>
                          <div className="relative h-2 rounded-full bg-white/5 overflow-hidden">
                            <div
                              className="absolute left-0 top-0 h-full rounded-full"
                              style={{
                                width: `${pct}%`,
                                background: "linear-gradient(90deg,#FF6A00,#FFA500)",
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="text-[11px] text-white/40">No probability on file yet</div>
                      )}

                      {item.sponsor?.name && (
                        <div className="text-[11px] text-white/45 pt-3 mt-3 border-t border-white/5 truncate">
                          <span className={`px-1 py-0.5 rounded text-[9px] mr-1 border ${partyClass(item.sponsor.party)}`}>
                            {item.sponsor.party || "?"}
                          </span>
                          {item.sponsor.name}
                        </div>
                      )}
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* LIVE SIGNALS strip */}
      <section className="border-b border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-400" />
              Live Signals
            </h2>
            <Link to="/signals" className="text-sm font-semibold text-orange-300 hover:text-orange-200" data-testid="signals-see-all">
              See all signals →
            </Link>
          </div>
          {featureMode("live_signals") === "none" ? (
            <LockedPanel feature="live_signals" tier="pro" title="Live Signals"
              blurb="Momentum shifts, surprise votes and floor-schedule signals as they happen." />
          ) : signals.length === 0 ? (
            <p className="text-white/55 text-sm">{loading ? "Polling intelligence feeds…" : "No significant movement detected yet."}</p>
          ) : (
            <>
            <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 scrollbar-thin md:grid md:gap-3 md:overflow-visible md:pb-0 md:mx-0 md:px-0 md:grid-cols-2 lg:grid-cols-4 md:auto-rows-fr" data-testid="signals-grid"
              onScroll={(e) => { const el = e.currentTarget; const w = el.firstElementChild?.getBoundingClientRect().width || 1; setSignalIdx(Math.round(el.scrollLeft / (w + 12))); }}>
              {signals.slice(0, 8).map((s, i) => (
                <Link
                  key={s.billId + i}
                  to={`/bill/${s.billId}`}
                  onClick={saveScroll}
                  className="group block h-full snap-start shrink-0 w-[85vw] sm:w-[70vw] md:w-auto"
                  data-testid={`signal-${i}`}
                >
                  <Card
                    className={`p-4 rounded-lg border transition-all hover:-translate-y-[1px] h-full min-h-[128px] flex flex-col ${
                      s.type === "shift-up"
                        ? "border-emerald-500/30 hover:border-emerald-400"
                        : s.type === "shift-down"
                        ? "border-rose-500/30 hover:border-rose-400"
                        : "border-purple-500/30 hover:border-purple-400"
                    }`}
                    style={{ background: "var(--whip-surface)" }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {s.type === "shift-up" && <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />}
                      {s.type === "shift-down" && <TrendingDown className="w-3.5 h-3.5 text-rose-400" />}
                      {s.type === "stalled" && <Sparkles className="w-3.5 h-3.5 text-purple-400" />}
                      <span className="text-[10px] uppercase tracking-wider font-bold text-white/45">
                        {s.type === "shift-up" ? "Probability surge" : s.type === "shift-down" ? "Probability drop" : "Stalled"}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-white leading-snug group-hover:text-orange-200 transition-colors line-clamp-2">
                      {s.headline}
                    </p>
                    {s.billTitle && (
                      <p className="text-xs text-white/45 mt-1.5 line-clamp-2">{s.billTitle}</p>
                    )}
                  </Card>
                </Link>
              ))}
            </div>
            <div className="flex md:hidden justify-center gap-1.5 mt-1" data-testid="signals-dots">
              {signals.slice(0, 8).map((s, i) => (
                <span key={s.billId + i} className={`h-1.5 rounded-full transition-all ${i === signalIdx ? "w-5 bg-orange-400" : "w-1.5 bg-white/25"}`} />
              ))}
            </div>
            </>
          )}
        </div>
      </section>

      {/* JUST INTRODUCED, bills filed in the last 72h, grouped by policy area.
          Surfaces brand-new bills WHIP has auto-classified before Congress.gov
          has assigned an official policy area. */}
      {justIntroduced && (justIntroduced.totalBills || 0) > 0 && (
        <section className="border-b border-white/5 py-12" data-testid="just-introduced-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-orange-400" />
                  Just Introduced
                </h2>
                <p className="text-sm text-white/55 mt-1">
                  <span className="font-bold text-white" data-testid="just-introduced-total">{justIntroduced.totalBills}</span>{" "}
                  bills filed in the last {justIntroduced.windowHours >= 72 ? `${Math.round(justIntroduced.windowHours / 24)} days` : `${Math.round(justIntroduced.windowHours)}h`}, grouped by policy area.
                </p>
              </div>
              <Link
                to="/legislation?introducedWithin=72"
                className="text-sm font-semibold text-orange-300 hover:text-orange-200"
                data-testid="just-introduced-see-all"
              >
                See all new bills →
              </Link>
            </div>

            <div
              className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
              data-testid="just-introduced-groups"
            >
              {(justIntroduced.groups || []).slice(0, 6).map((g) => (
                <Card
                  key={g.policyArea}
                  className="p-4 rounded-lg border border-white/10 hover:border-orange-500/30 transition-colors"
                  style={{ background: "var(--whip-surface)" }}
                  data-testid={`just-introduced-group-${g.policyArea.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                >
                  <div className="flex items-baseline justify-between mb-3 pb-2 border-b border-white/10">
                    <h3 className="text-sm font-bold text-white leading-tight pr-2 line-clamp-2">
                      {g.policyArea}
                    </h3>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs font-bold tabular-nums text-orange-300">
                        {g.count}
                      </span>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {g.bills.map((b) => (
                      <li key={b.id}>
                        <Link
                          to={`/bill/${b.id}`}
                          onClick={saveScroll}
                          className="block group/bill"
                          data-testid={`just-introduced-bill-${b.id}`}
                        >
                          <div className="flex items-baseline gap-2">
                            <span className="text-[10px] font-mono text-orange-300/70 shrink-0 tabular-nums">
                              {b.billNumber}
                            </span>
                            <span className="text-xs text-white/85 group-hover/bill:text-orange-200 transition-colors line-clamp-2 leading-snug">
                              {b.title || "Untitled"}
                            </span>
                          </div>
                          <div className="text-[10px] text-white/40 mt-0.5 ml-[3.5rem]">
                            {b.introducedDate}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TRENDING LEGISLATION + TOP LEGISLATORS */}
      <section className="border-b border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          {/* Trending, 2 cols */}
          <div className="lg:col-span-2">
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
                <Activity className="w-5 h-5 text-orange-400" />
                Trending Legislation
                <span className="text-[10px] font-mono font-normal text-white/40 uppercase tracking-wider hidden sm:inline" title="Ranked by momentum: recent floor/committee action, cosponsor velocity, forecast shifts, news mentions and WHIP watch activity, not just latest update.">· by momentum</span>
              </h2>
              <Link to="/legislation" className="text-sm font-semibold text-orange-300 hover:text-orange-200" data-testid="trending-see-all">
                See all bills →
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2" data-testid="trending-grid">
              {trending.slice(0, 4).map((b) => (
                <Link
                  key={b.id}
                  to={`/bill/${b.id}`}
                  onClick={saveScroll}
                  className="block group"
                  data-testid={`trending-${b.id}`}
                >
                  <Card
                    className="p-4 rounded-lg border border-white/10 hover:border-orange-500/40 transition-colors h-full"
                    style={{ background: "var(--whip-surface)" }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-indigo-500/15 text-indigo-300 border-indigo-500/30 border text-[10px] px-1.5 py-0">
                        {b.billNumber}
                      </Badge>
                      <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 border text-[10px] px-1.5 py-0">
                        {b.status}
                      </Badge>
                    </div>
                    <h3 className="text-sm font-semibold text-white leading-snug line-clamp-2 group-hover:text-orange-200">
                      {b.title}
                    </h3>
                    <p className="text-[11px] text-white/45 mt-2 uppercase tracking-wider">
                      {b.congress}th Congress · {b.type}
                    </p>
                    {b.momentumReasons?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2" data-testid={`trending-reasons-${b.id}`}>
                        {b.momentumReasons.map((r) => (
                          <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-300 border border-orange-500/25">{r}</span>
                        ))}
                      </div>
                    )}
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* Top legislators, leaderboard preview */}
          <div>
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
                <Trophy className="w-5 h-5 text-orange-400" />
                Power Players
              </h2>
              <Link to="/rankings" className="text-sm font-semibold text-orange-300 hover:text-orange-200" data-testid="rankings-see-all">
                Full rankings →
              </Link>
            </div>
            <Card className="p-2 rounded-lg border border-white/10" style={{ background: "var(--whip-surface)" }}>
              <ol className="divide-y divide-white/5">
                {topLegs.slice(0, 5).map((l, i) => (
                  <li key={l.id} data-testid={`top-leg-${i + 1}`}>
                    <Link
                      to={`/legislator/${l.id}`}
                      onClick={saveScroll}
                      className="flex items-center gap-3 p-2 rounded-md hover:bg-white/[0.04] transition-colors"
                    >
                      <span className="text-lg font-black w-6 text-center tabular-nums text-orange-400">
                        {i + 1}
                      </span>
                      <img
                        src={l.photo}
                        alt={l.name}
                        className="w-9 h-9 rounded-full object-cover border border-white/10"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{l.name}</div>
                        <div className="text-[11px] text-white/45 truncate">
                          {l.state} · {l.chamber}
                        </div>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${partyClass(l.party)}`}>
                        {l.party}
                      </span>
                      <span className="text-sm font-black tabular-nums whip-orange-text w-10 text-right">
                        {Number(l.stats?.bipartisanIndex || 0).toFixed(1)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </Card>
            <p className="text-[10px] text-white/40 mt-2">
              Sorted by bipartisan index, switch metrics on the Rankings page.
            </p>
          </div>
        </div>
      </section>

      {/* POWER MOVEMENT, quick links */}
      <section className="border-b border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
              <Network className="w-5 h-5 text-orange-400" />
              Power Movement
            </h2>
            <span className="text-xs text-white/45">Where the alliances bend</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: "/factions", icon: Network, label: "Alliance Map", sub: "Voting blocs as a graph", testId: "qlink-factions" },
              { to: "/parties", icon: Briefcase, label: "Party Aggregates", sub: "D vs R vs I at a glance", testId: "qlink-parties" },
              { to: "/committees", icon: Landmark, label: "Committees", sub: "Where bills live or die", testId: "qlink-committees" },
              { to: "/compare", icon: GitCompare, label: "Compare", sub: "Two legislators, side-by-side", testId: "qlink-compare" },
            ].map((q) => (
              <Link
                key={q.to}
                to={q.to}
                data-testid={q.testId}
                className="group block"
              >
                <Card
                  className="p-5 rounded-lg border border-white/10 hover:border-orange-500/40 transition-all hover:-translate-y-[2px] h-full"
                  style={{ background: "var(--whip-surface)" }}
                >
                  <q.icon className="w-6 h-6 text-orange-400 mb-3" />
                  <div className="text-base font-bold text-white group-hover:text-orange-200">{q.label}</div>
                  <div className="text-xs text-white/55 mt-0.5">{q.sub}</div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Power Movement → end of page (CTA removed per user request). */}
    </div>
  );
}
